import React, { useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Tooltip,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider,
    CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import EnumsService from '../../Services/enums.service';
import AutocompleteSelect from '../Widgets/AutocompleteSelect/AutocompleteSelect';

/**
 * Monthly-charges Excel import: Download Template → Upload → Preview (server) → Import (commit).
 *
 * The server resolves a missing caseId from the client name. Rows whose name could not be
 * matched (needsCaseId) get an inline client picker so the user can add the caseId before commit.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - accountantId: string
 *  - token: string
 *  - clientOptions: [{ value, label }]   (caseId -> name)
 *  - defaultCaseId: string | null        (fallback caseId for non-leading business)
 *  - onImported: () => void              (called after a successful commit, e.g. to refetch)
 */
const MonthlyChargesExcelImportModal = ({
    open,
    onClose,
    accountantId,
    token,
    clientOptions = [],
    defaultCaseId = '',
    onImported,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const fileInputRef = useRef(null);

    const [fileName, setFileName] = useState('');
    const [rows, setRows] = useState([]);
    const [parseError, setParseError] = useState('');
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const caseIdOptions = useMemo(
        () => clientOptions.map((c) => ({ value: String(c.value), label: c.label })),
        [clientOptions]
    );
    const caseIdToName = useMemo(() => {
        const m = new Map();
        clientOptions.forEach((c) => m.set(String(c.value), c.label));
        return m;
    }, [clientOptions]);

    const rowIsValid = (r) =>
        !!r.caseId &&
        !!r.startDate &&
        Number(r.amount) !== 0 &&
        !!(r.description && String(r.description).trim());

    const validRows = useMemo(() => rows.filter(rowIsValid), [rows]);
    const invalidRowsCount = rows.length - validRows.length;

    const fmtMonth = (d) => (d ? dayjs(d).format('MM/YYYY') : '');

    const handleClear = () => {
        setRows([]);
        setFileName('');
        setParseError('');
    };

    const handleClose = () => {
        if (isImporting || isPreviewing) return;
        handleClear();
        onClose?.();
    };

    const handleDownloadTemplate = async () => {
        try {
            await EnumsService.DownloadMonthlyChargesTemplate(token);
        } catch (err) {
            console.error('Failed to download monthly charges template', err);
            setParseError(t('FailedToDownloadTemplate') || 'Failed to download template');
        }
    };

    const handleFileChange = async (e) => {
        setParseError('');
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setIsPreviewing(true);
        try {
            const result = await EnumsService.PreviewMonthlyChargesImport(
                accountantId,
                file,
                token,
                defaultCaseId || null
            );
            const previewRows = (result?.rows || []).map((r) => ({
                ...r,
                // ensure a stable client-side key & normalized caseId string
                caseId: r.caseId ? String(r.caseId) : '',
            }));
            setRows(previewRows);
            if ((result?.rows || []).length === 0) {
                setParseError(
                    (result?.errorMessages && result.errorMessages[0]) ||
                    t('NoValidRowsToImport') ||
                    'No rows found'
                );
            }
        } catch (err) {
            console.error('Monthly charges preview failed', err);
            setParseError(err?.message || t('FailedToReadFile') || 'Failed to read file');
            setRows([]);
        } finally {
            setIsPreviewing(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSetCaseId = (index, option) => {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r;
                const caseId = option?.value ? String(option.value) : '';
                return {
                    ...r,
                    caseId,
                    resolvedClientName: caseId ? caseIdToName.get(caseId) || option?.label || '' : '',
                    needsCaseId: !caseId,
                    caseIdResolvedByName: false,
                    errorMessage: caseId ? '' : r.errorMessage,
                };
            })
        );
    };

    const handleRemoveRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const handleImport = async () => {
        if (validRows.length === 0) return;
        setIsImporting(true);
        try {
            const result = await EnumsService.CommitMonthlyChargesImport(accountantId, validRows, token);
            const processed = result?.processedCount || 0;
            const errors = result?.errorCount || 0;
            const skipped = result?.skippedCount || 0;

            if (processed > 0) {
                // handled by parent toast via onImported; nothing else here
            }
            if (onImported) onImported({ processed, errors, skipped, errorMessages: result?.errorMessages || [] });
            if (processed > 0 && errors === 0 && skipped === 0) {
                handleClear();
                onClose?.();
            }
        } catch (err) {
            console.error('Monthly charges import failed', err);
            setParseError(err?.message || t('ImportFailed') || 'Import failed');
        } finally {
            setIsImporting(false);
        }
    };

    const borderBoxSx = {
        boxSizing: 'border-box',
        '& *, & *::before, & *::after': { boxSizing: 'border-box' },
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            aria-labelledby="monthly-charges-import-title"
            PaperProps={{ sx: { ...borderBoxSx, width: '100%', maxWidth: '1150px', overflow: 'hidden' } }}
            sx={borderBoxSx}
        >
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <Tooltip title={t('Cancel')}>
                    <IconButton onClick={handleClose} disabled={isImporting || isPreviewing}>
                        <CancelIcon sx={{ color: '#304FFF' }} />
                    </IconButton>
                </Tooltip>
            </Box>

            <DialogTitle id="monthly-charges-import-title" sx={{ textAlign: 'center', pr: 5 }}>
                {t('ImportMonthlyChargesFromExcel') || t('ImportFromExcel')}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('UploadFileThenPreviewAndImport') || ''}
                </Typography>
            </DialogTitle>

            <DialogContent className={direction} sx={{ pt: 1, overflowX: 'hidden' }}>
                <Box
                    sx={{
                        width: '100%',
                        border: '1px dashed #c0c7f5',
                        borderRadius: '8px',
                        p: 2,
                        backgroundColor: '#fafbff',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {t('ImportMonthlyChargesFromExcel') || t('ImportFromExcel')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadTemplate}
                                sx={{ borderRadius: '8px', '& .MuiButton-startIcon': { margin: 0 }, gap: 1 }}
                            >
                                {t('DownloadTemplate')}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={isPreviewing ? <CircularProgress size={16} /> : <UploadFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isPreviewing}
                                sx={{ borderRadius: '8px', '& .MuiButton-startIcon': { margin: 0 }, gap: 1 }}
                            >
                                {t('UploadFile')}
                            </Button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                hidden
                                accept=".xls,.xlsx,.csv"
                                onChange={handleFileChange}
                            />
                        </Box>
                    </Box>

                    {fileName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {fileName}
                        </Typography>
                    )}

                    {parseError && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            {parseError}
                        </Typography>
                    )}

                    {rows.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                                <Chip size="small" color="primary" label={`${t('Total') || 'סה"כ'}: ${rows.length}`} />
                                <Chip size="small" color="success" label={`${t('Valid') || 'תקינות'}: ${validRows.length}`} />
                                {invalidRowsCount > 0 && (
                                    <Chip size="small" color="error" label={`${t('ValidationErrors') || 'שגיאות'}: ${invalidRowsCount}`} />
                                )}
                                <Box sx={{ flex: 1 }} />
                                <Button size="small" color="inherit" onClick={handleClear}>
                                    {t('Clear') || 'נקה'}
                                </Button>
                            </Box>

                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>{t('Status') || 'סטטוס'}</TableCell>
                                            <TableCell sx={{ minWidth: 220 }}>{t('CaseId') || 'מזהה תיק'}</TableCell>
                                            <TableCell>{t('Client') || 'לקוח'}</TableCell>
                                            <TableCell>{t('StartDate') || 'חודש התחלה'}</TableCell>
                                            <TableCell>{t('EndDate') || 'חודש סיום'}</TableCell>
                                            <TableCell>{t('Amount') || 'סכום'}</TableCell>
                                            <TableCell>{t('Description') || 'תיאור'}</TableCell>
                                            <TableCell>{t('PriceIncludingVAT') || 'כולל מע"מ'}</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((r, i) => {
                                            const valid = rowIsValid(r);
                                            const needsCaseId = !r.caseId || r.needsCaseId;
                                            return (
                                                <TableRow key={i} hover sx={{ backgroundColor: valid ? 'inherit' : '#fff5f5' }}>
                                                    <TableCell>{r.lineNumber}</TableCell>
                                                    <TableCell>
                                                        {valid ? (
                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                        ) : (
                                                            <Tooltip title={r.errorMessage || (needsCaseId ? (t('AddCaseId') || 'הוסף מזהה תיק') : '')}>
                                                                <ErrorIcon color="error" fontSize="small" />
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 220 }}>
                                                        {needsCaseId ? (
                                                            <AutocompleteSelect
                                                                options={caseIdOptions}
                                                                selectedValues={
                                                                    caseIdOptions.find((o) => o.value === String(r.caseId)) || null
                                                                }
                                                                onChange={(_, newVal) => handleSetCaseId(i, newVal)}
                                                                isMultiple={false}
                                                                width="100%"
                                                                placeholder={t('SelectCustomer') || 'בחר לקוח'}
                                                            />
                                                        ) : (
                                                            <span>{r.caseId}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{r.resolvedClientName || r.clientName || ''}</TableCell>
                                                    <TableCell>{fmtMonth(r.startDate)}</TableCell>
                                                    <TableCell>{fmtMonth(r.endDate)}</TableCell>
                                                    <TableCell>{r.amount}</TableCell>
                                                    <TableCell>{r.description}</TableCell>
                                                    <TableCell>{r.priceIncludingVAT ? (t('Yes') || 'כן') : (t('No') || 'לא')}</TableCell>
                                                    <TableCell>
                                                        <IconButton size="small" onClick={() => handleRemoveRow(i)}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'flex-end', p: 2, gap: 1 }}>
                <Button onClick={handleClose} disabled={isImporting || isPreviewing} variant="outlined" sx={{ borderRadius: '8px' }}>
                    {t('Close') || t('Cancel')}
                </Button>
                <Button
                    variant="contained"
                    startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    disabled={validRows.length === 0 || isImporting || isPreviewing}
                    onClick={handleImport}
                    sx={{
                        borderRadius: '8px',
                        '& .MuiButton-startIcon': { margin: 0 },
                        gap: 1,
                        backgroundColor: '#304FFF',
                        '&:hover': { backgroundColor: '#1565c0' },
                    }}
                >
                    {t('Import')} ({validRows.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MonthlyChargesExcelImportModal;
